import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { bootstrapCameraKit, createMediaStreamSource, Transform2D } from '@snap/camera-kit';
import ARIcon from './icons/ARIcon';
import SnapchatIcon from './icons/SnapchatIcon';
import FullColumnLayout from './FullColumnLayout';
import { breakpoints, zIndexes } from '../utils/styles';
import { getSnapchatApiToken, getSnapchatLenses, isSnapchatEnabled } from '../reducers';
import LogoLoading from './LogoLoading';
import { snapchatLensesRoute } from '../routes';

// Constantes
const CANVAS_DIMENSIONS = {
  width: 1280,
  height: 720
};

const CANVAS_MAX_WIDTH = {
  desktop: 375,
  tablet: 300
};

const Z_INDEX = {
  canvas: zIndexes.header - 10,
  controls: zIndexes.header - 5,
  flash: zIndexes.sidebar + 1000,
  fullscreen: zIndexes.header + 100
};

const BUTTON_SIZES = {
  footer: { width: 2, height: 2 },
  lensDisplay: { width: 65, height: 65 },
  snapchatIcon: { width: 20, height: 20 }
};

const LAYOUT = {
  maxContentWidth: 900,
  canvasBorderRadius: 15
};

// Constantes de timing y configuración
const TIMING = {
  sessionSetupDelay: 200,
  headerCheckInterval: 100,
  resizeDebounce: 250,
  flashDuration: 400,
  captureDelay: 100
};

const CAMERA_CONFIG = {
  mobile: {
    video: { 
      facingMode: 'user',
      width: { ideal: 1080, max: 1280 },
      height: { ideal: 960, max: 1920 }
    }
  },
  desktop: {
    video: { 
      facingMode: 'user',
      width: 9999,
      aspectRatio: 0.5625
    }
  }
};

// Mixins para estilos comunes
const mixins = {
  flexCenter: `
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  flexCenterColumn: `
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  fullSize: `
    width: 100%;
    height: 100%;
  `,
  absoluteFullscreen: `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `,
  circularButton: `
    border-radius: 50%;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  `
};

const ContentContainer = styled.div`
  ${mixins.flexCenterColumn}
  padding: ${props => props.fullWidth ? '0' : '0 20px'};
  margin-top: ${props => props.fullWidth ? '0' : '30px'};
  width: 100%;
  max-width: ${props => props.fullWidth ? '100%' : '900px'};
  box-sizing: border-box;
  margin: ${props => props.fullWidth ? '0' : '0 auto'};
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    max-width: none;
    
    &:has(.fullscreen-mode) {
      padding: 0;
      margin: 0;
      width: 100%;
      max-width: 100%;
      height: 100vh;
      position: relative;
    }
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: ${props => props.fullWidth ? '100%' : 'inherit'};
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    ${mixins.flexCenterColumn}
    
    &:has(.fullscreen-mode) {
      height: 100vh;
    }
  }
`;

const TitleSectionWrapper = styled.div`
  display: flex;
  justify-content: ${props => props.inLensView ? 'flex-start' : 'center'};
  margin: 30px 0 20px 0;
  width: 100%;
  
  @media screen and (max-width: ${breakpoints.tablet}px) {
    margin: 20px 0 15px 0;
  }
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    margin: 15px 0 10px 0;
    justify-content: flex-start;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  .title-with-icon {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 0.2em;
    line-height: 1;
    font-family: var(--ff-title);
    font-weight: 500;
    font-size: 55px;
    letter-spacing: 0px;
    text-transform: uppercase;
    text-align: ${props => props.inLensView ? 'left' : 'center'};

    /* Reglas para inLeftColumn en desktop */
    ${props => props.inLeftColumn && `
      @media screen and (max-width: ${breakpoints.desktop}px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 0;
        line-height: 0.9;
      }
    `}
    
    /* Reglas para temas específicos */
    ${props => props.inLeftColumn && `
      body.theme-platinum &,
      body.theme-market & {
        flex-direction: column;
        align-items: flex-start;
        gap: 0;
        line-height: 0.9;
      }
    `}

    /* Tamaños de fuente responsivos para temas específicos */
    @media screen and (max-width: ${breakpoints.desktop}px) {    
      body.theme-platinum &, 
      body.theme-market & {
        font-size: 45px;
      }
    }

    @media screen and (max-width: ${breakpoints.tablet}px) {    
      body.theme-platinum &, 
      body.theme-market & {
        font-size: 35px;
      }
    }

    @media screen and (max-width: ${breakpoints.phone}px) {
      text-align: left;
      justify-content: flex-start;
      flex-direction: column;
      align-items: flex-start;
      gap: 0;
    }
  }
  
  .title-line {
    display: flex;
    align-items: center;
    gap: 0.3em;
    
    /* Reglas para inLeftColumn en desktop */
    ${props => props.inLeftColumn && `
      @media screen and (max-width: ${breakpoints.desktop}px) {
        width: 100%;
        
        &.second-line {
          justify-content: space-between;
        }
      }
    `}
    
    /* Reglas para temas específicos */
    ${props => props.inLeftColumn && `
      body.theme-platinum &,
      body.theme-market & {
        width: 100%;
        
        &.second-line {
          justify-content: space-between;
        }
      }
    `}
    
    @media screen and (max-width: ${breakpoints.phone}px) {
      width: 100%;
      
      &.second-line {
        justify-content: space-between;
      }
    }
  }
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    align-items: flex-start;
  }
`;

const ARIconStyled = styled(ARIcon)`
  width: 0.9em;
  height: 0.9em;
  display: block;
  flex-shrink: 0;
`;

const PoweredByStyled = styled.div`
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  margin-top: 5px;
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    justify-content: flex-start;
    text-align: left;
    width: 100%;
  }
`;

const ResponsiveSnapchatIcon = styled(SnapchatIcon)`
  margin-left: 0.25em;
`;

const Subtitle = styled.div`
  font-size: 16px;
  letter-spacing: 0px;
  text-align: center;
  display: ${props => props.hide ? 'none' : 'block'};
  width: 100%;
  margin: 0 auto;
  max-width: ${LAYOUT.maxContentWidth}px;
  padding: 0 20px;
  clear: both;
  line-height: 1.2;

  @media screen and (max-width: ${breakpoints.phone}px) {
    font-size: 12px;
    max-width: 100%;
    margin: 10px auto;
    padding-left: 0;
    padding-right: 0;
    text-align: left;
  }
`;

const LensesGrid = styled.div`
  margin-top: 2rem;
  text-align: left;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, 110px);
  column-gap: 4em;
  row-gap: 2em;
  justify-content: center;

  @media screen and (max-width: ${breakpoints.tablet}px) {
    column-gap: 2em;
  }
`;

const LensItem = styled.div`
  cursor: pointer;
  display: inline-block;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  text-align: center;
  
  &:hover {
    transform: scale(1.1);
  }
  
  img {
    width: 100%;
    border-radius: 50%;
    margin-bottom: 8px;
  }
`;

const LensName = styled.div`
  font-size: 15px;
  color: var(--text-primary);
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 4px;
  line-height: 1.1;
  font-weight: 700;
  text-transform: uppercase;
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    font-size: 12px;
  }
`;

const PreviewContainer = styled.div`
  ${mixins.flexCenterColumn}
  width: 100%;
  margin: 0;
  display: ${props => props.visible ? 'flex' : 'none'};
  justify-content: flex-start;
  min-height: 60vh;
  margin-top: 75px;
  
  @media screen and (max-width: ${breakpoints.desktop}px) {
    margin-top: 70px;
  }
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    justify-content: center;
    margin-top: 0;
  }
`;

const CanvasContainer = styled.div`
  overflow: hidden;
  border-radius: ${LAYOUT.canvasBorderRadius}px;
  margin-top: 0;
  position: relative;
  width: 100%;
  max-width: ${CANVAS_MAX_WIDTH.desktop}px;
  aspect-ratio: 9/16;

  @media screen and (max-width: ${breakpoints.desktop}px) {
    max-width: ${CANVAS_MAX_WIDTH.tablet}px;
  }
  
  /* Estilos de fullscreen unificados */
  @media screen and (max-width: ${breakpoints.phone}px),
  &.fullscreen-canvas {
    ${mixins.absoluteFullscreen}
    max-width: none;
    margin: 0;
    z-index: ${Z_INDEX.canvas};
    border-radius: 0;
    aspect-ratio: auto;
  }
  
  .canvas-wrapper {
    ${mixins.fullSize}
    position: relative;
    overflow: hidden;
  }
`;

const Canvas = styled.canvas`
  position: absolute;
  left: 0;
  top: 0;
  ${mixins.fullSize}
  border-radius: inherit;
`;

const ControlsContainer = styled.div`
  ${mixins.flexCenter}
  margin-top: 1rem;
  padding: 12px 16px;
  background: none;
  width: 100%;
  max-width: ${CANVAS_MAX_WIDTH.tablet}px;
  
  @media screen and (max-width: ${breakpoints.tablet}px) {
    margin-top: 0.5rem;
    padding: 8px;
  }
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    display: none;
  }
`;

const SelectedLensName = styled.p`
  font-size: 16px;
  margin: 0;
  text-align: center;
  color: var(--text-primary);
`;

const ErrorContainer = styled.div`
  text-align: center;
  margin-top: 2rem;
`;

const ErrorMessage = styled.p`
  color: #FE142F;
  font-size: 1.2rem;
`;

const TechnicalErrorDetails = styled.pre`
  margin-top: 1rem;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: left;
  overflow-x: auto;
  max-width: 100%;
`;

const LensesContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 200px;
  margin-top: 1rem;
  margin-bottom: 3rem;
  clear: both;
`;

const TwoColumnsContainer = styled.div`
  display: ${props => props.visible ? 'flex' : 'none'};
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: 0;
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    flex-direction: column;
    position: relative;
    height: 100vh;
    overflow: hidden;
    
    &.fullscreen-mode {
      ${mixins.absoluteFullscreen}
      z-index: ${Z_INDEX.fullscreen};
      background-color: #000;
    }
  }
`;

const LeftColumn = styled.div`
  flex: 0 0 450px;
  padding: 20px;
  
  @media screen and (max-width: ${breakpoints.desktop}px) {
    flex: 0 1 250px;
    min-width: 40%;
    padding: 15px;
    ${mixins.flexCenterColumn}
    align-items: flex-start;
  }
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    display: none;
  }
`;

const RightColumn = styled.div`
  flex: 0.7;
  ${mixins.flexCenter}
  align-items: flex-start;
  
  @media screen and (max-width: ${breakpoints.desktop}px) {
    flex: 1 0 400px;
    padding-right: 20px;
  }
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    ${mixins.fullSize}
    padding: 0;
    align-items: center;
  }
`;

const BackLink = styled.a`
  display: flex;
  align-items: center;
  color: var(--text-primary);
  font-size: 18px;
  margin-bottom: 20px;
  cursor: pointer;
  
  svg {
    margin-right: 8px;
  }
  
  @media screen and (max-width: ${breakpoints.tablet}px) {
    margin-bottom: 10px;
  }
`;

const CanvasHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: 20px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: ${Z_INDEX.controls};
  background: linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 100%);
  pointer-events: auto;
`;

const CanvasBackButton = styled.button`
  background: none;
  border: none;
  padding: 10px;
  margin: 0;
  cursor: pointer;
  color: white;
  ${mixins.flexCenter}
  font-size: 24px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const CanvasPoweredBy = styled.div`
  display: flex;
  align-items: center;
  color: white;
  font-size: 12px;
  
  svg {
    margin-left: 5px;
    width: ${BUTTON_SIZES.snapchatIcon.width}px;
    height: ${BUTTON_SIZES.snapchatIcon.height}px;
    fill: white;
  }
`;

const CanvasFooter = styled.div`
  position: absolute;
  bottom: 40px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  z-index: ${Z_INDEX.controls};
  pointer-events: auto;
  
  @media screen and (max-width: ${breakpoints.phone}px) {
    position: fixed;
    z-index: 9999;
    bottom: 60px;
  }
`;

const FooterButton = styled.button`
  ${mixins.circularButton}
  width: ${BUTTON_SIZES.footer.width}em;
  height: ${BUTTON_SIZES.footer.height}em;
  background-color: rgb(77, 77, 77);
  color: white;
  font-size: 20px;
  transition: all 0.2s ease;

  @media screen and (min-width: ${breakpoints.phone}px) {
    &:active {
      background-color: rgba(200, 200, 200, 0.9);
      transform: scale(0.95);
    }
  }

  &:focus {
    outline: none;
  }
`;

const LensDisplayButton = styled.div`
  ${mixins.circularButton}
  width: ${BUTTON_SIZES.lensDisplay.width}px;
  height: ${BUTTON_SIZES.lensDisplay.height}px;
  background-color: white;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  
  img {
    width: 105%;
    height: 105%;
    object-fit: cover;
    border-radius: 50%;
    border: none;
    outline: none;
  }
`;

const isProduction = process.env.NODE_ENV === 'production';

// Funciones helper centralizadas
const resetBodyStyles = () => {
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.height = '';
  document.body.style.top = '';
  document.body.style.left = '';
};

const setFullscreenBodyStyles = () => {
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  document.body.style.height = '100%';
  document.body.style.top = '0';
  document.body.style.left = '0';
};

const toggleHeaderVisibility = (shouldHide) => {
  const header = document.querySelector('header');
  const appNavigation = document.querySelector('.app-navigation');
  
  if (header) header.style.display = shouldHide ? 'none' : '';
  if (appNavigation) appNavigation.style.display = shouldHide ? 'none' : '';
};

const createPhotoFlash = () => {
  const flash = document.createElement('div');
  flash.setAttribute('id', 'photo-flash');
  flash.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: white;
    opacity: 0.8;
    z-index: ${Z_INDEX.flash};
    pointer-events: none;
    transition: opacity 0.4s ease;
  `;
  return flash;
};

const downloadPhoto = (photoData) => {
  return fetch(photoData)
    .then(res => res.blob())
    .then(blob => {
      const blobUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = `cinemex-ar-${Date.now()}.jpg`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(blobUrl);
      }, TIMING.captureDelay);
    });
};

const getCameraConstraints = (isMobile, facing = 'user') => {
  const config = isMobile ? CAMERA_CONFIG.mobile : CAMERA_CONFIG.desktop;
  return {
    ...config,
    video: {
      ...config.video,
      facingMode: facing
    }
  };
};

const calculateRenderSize = (isMobile, camWidth, camHeight) => {
  if (isMobile) {
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    
    if (!camWidth || !camHeight) {
      // Para móviles, mejor resolución manteniendo control del zoom
      return { w: Math.min(viewportWidth * 0.9, 900), h: Math.min(viewportHeight * 0.9, 1600) };
    }
    
    const streamAspect = camWidth / camHeight;
    const screenAspect = viewportWidth / viewportHeight;
    
    // Mejor resolución manteniendo control del zoom
    const maxWidth = Math.min(viewportWidth * 0.9, 900);
    const maxHeight = Math.min(viewportHeight * 0.9, 1600);
    
    if (streamAspect > screenAspect) {
      const h = maxHeight;
      return { w: Math.min(h * streamAspect, maxWidth), h };
    } else {
      const w = maxWidth;
      return { w, h: Math.min(w / streamAspect, maxHeight) };
    }
  } else {
    if (!camWidth || !camHeight) {
      return { w: 1080, h: 1920 };
    } else if (camWidth > camHeight) {
      return { w: camWidth, h: camWidth * (16/9) };
    } else {
      return { w: camWidth, h: camHeight };
    }
  }
};

const handleError = (error, setErrorFunction) => {
  if (isProduction) {
    setErrorFunction({
      message: "Ha ocurrido un error. Por favor, intenta de nuevo más tarde",
      details: null
    });
  } else {
    setErrorFunction({
      message: `Error: ${error.message || 'Unknown error'}`,
      details: error.stack || String(error)
    });
  }
};

const TitleSection = ({ inLensView = false, inLeftColumn = false }) => {
  return (
    <TitleSectionWrapper inLensView={inLensView}>
      <TitleContainer inLensView={inLensView} inLeftColumn={inLeftColumn}>
        <div className="title-with-icon">
          <div className="title-line first-line">
            <span>REALIDAD</span>
          </div>
          <div className="title-line second-line">
            <span>AUMENTADA</span>
            <ARIconStyled />
          </div>
        </div>
        <PoweredByStyled>
          Powered by <ResponsiveSnapchatIcon/>
        </PoweredByStyled>
      </TitleContainer>
    </TitleSectionWrapper>
  );
};

const CanvasControls = ({ onBack, onCameraSwitch, onCapture, selectedLens }) => {
  useEffect(() => {
    toggleHeaderVisibility(true);
    return () => {};
  }, []);
  
  return (
    <>
      <CanvasHeader>
        <CanvasBackButton onClick={onBack}>
          <i className="fal fa-arrow-left"></i>
        </CanvasBackButton>
        
        <CanvasPoweredBy>
          Powered by <ResponsiveSnapchatIcon/>
        </CanvasPoweredBy>
      </CanvasHeader>
      
      <CanvasFooter>
        <FooterButton onClick={onCapture} title="Tomar foto">
          <i className="fal fa-camera"></i>
        </FooterButton>
        
        <LensDisplayButton>
          {selectedLens && selectedLens.iconUrl ? (
            <img src={selectedLens.iconUrl} alt={selectedLens.name} />
          ) : (
            <div style={{ 
              width: `${BUTTON_SIZES.lensDisplay.width - 7}px`, 
              height: `${BUTTON_SIZES.lensDisplay.height - 7}px`, 
              borderRadius: '50%', 
              backgroundColor: '#FE142F',
              border: '2px solid white'
            }} />
          )}
        </LensDisplayButton>
        
        <FooterButton onClick={onCameraSwitch} title="Cambiar cámara">
          <i className="fal fa-sync-alt"></i>
        </FooterButton>
      </CanvasFooter>
    </>
  );
};

const shouldUseBackCamera = (lens) => {
  return lens && lens.defaultCamera === 'back';
};

const getOppositeCameraFacing = (currentFacing) => {
  return currentFacing === 'user' ? 'environment' : 'user';
};

const SnapchatLensesContent = (props) => {
  const [cameraKit, setCameraKit] = useState(null);
  const [session, setSession] = useState(null);
  const [lenses, setLenses] = useState([]);
  const [selectedLens, setSelectedLens] = useState(null);
  const [error, setError] = useState(null);
  const [cameraFacing, setCameraFacing] = useState('user');
  const [showLenses, setShowLenses] = useState(true);
  const [showLoading, setShowLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isFullscreenMode, setIsFullscreenMode] = useState(false);
  
  const canvasRef = useRef(null);
  const lensesGridRef = useRef(null);
  const activeStreamRef = useRef(null);
  const cameraKitInstanceRef = useRef(null);
  
  // Refs to track current values for cleanup
  const sessionRef = useRef(null);
  const selectedLensRef = useRef(null);
  const isMountedRef = useRef(true); // Track if component is mounted
  
  // Get slug from URL parameters if present
  const urlSlug = props.match && props.match.params && props.match.params.slug;
  
  // Helper function to safely update state only if component is mounted
  const safeSetState = (stateSetter, value) => {
    if (isMountedRef.current) {
      stateSetter(value);
    }
  };
  
  useEffect(() => {
    const checkIfMobile = () => {
      const nextIsMobile = window.innerWidth <= breakpoints.phone;
      safeSetState(setIsMobile, nextIsMobile);
    };
    
    checkIfMobile();
    
    let resizeTimer;
    const handleMobileResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkIfMobile, TIMING.resizeDebounce);
    };
    
    window.addEventListener('resize', handleMobileResize);
    
    return () => {
      window.removeEventListener('resize', handleMobileResize);
      clearTimeout(resizeTimer);
    };
  }, []);
  
  useEffect(() => {
    if (!props.isActive || cameraKitInstanceRef.current || error) return;

    const initCameraKit = async () => {
      try {
        if (!cameraKitInstanceRef.current) {
          const kit = await bootstrapCameraKit({ 
            apiToken: props.apiToken
          });
          cameraKitInstanceRef.current = kit;
          safeSetState(setCameraKit, kit);
        }
      } catch (err) {
        if (isMountedRef.current) {
          handleError(err, setError);
        } else {
          console.warn('Error initializing CameraKit (component unmounted):', err);
        }
      }
    };

    initCameraKit();
    
    return () => {
      if (session) {
        session.pause();
        if (selectedLens) session.removeLens();
      }
    };
  }, [props.isActive, props.apiToken, error]);

  useEffect(() => {
    if (!cameraKit || error) return;

    const setupSession = async () => {
      try {
        if (session) {
          return;
        }

        const canvas = document.getElementById('snapchat-camera-canvas');

        if (!canvas) {
          setTimeout(setupSession, TIMING.captureDelay);
          return;
        }
        
        try {
          canvas.width = CANVAS_DIMENSIONS.width;
          canvas.height = CANVAS_DIMENSIONS.height;
        } catch (err) {
          console.warn("Canvas already under SDK control");
        }
        
        const newSession = await cameraKit.createSession({ 
          liveRenderTarget: canvas 
        });
        
        safeSetState(setSession, newSession);
        
        await setMediaStream(newSession, cameraFacing);
        await loadLenses();
      } catch (err) {
        if (isMountedRef.current) {
          handleError(err, setError);
        } else {
          console.warn('Error setting up session (component unmounted):', err);
        }
      }
    };
    
    const timer = setTimeout(setupSession, TIMING.sessionSetupDelay);
    return () => clearTimeout(timer);
  }, [cameraKit, error, isMobile]);

  // Effect to auto-select lens based on URL slug
  useEffect(() => {
    if (urlSlug && lenses.length > 0 && !selectedLens) {
      // Case 1: Direct access to lens URL - auto-select the lens
      const targetLens = lenses.find(lens => {
        const backendConfig = lens.backendConfig;
        return backendConfig && backendConfig.slug === urlSlug;
      });
      
      if (targetLens) {
        handleLensSelect(targetLens);
      } else if (props.history) {
        props.history.replace(snapchatLensesRoute);
      }
    }
    
    // Case 2: User navigated back to main AR page - reset if coming from a lens with URL
    if (!urlSlug && selectedLens) {
      const selectedLensHasSlug = selectedLens.backendConfig && selectedLens.backendConfig.slug;
      
      if (selectedLensHasSlug) {
        // Coming back from a lens that had its own URL - reset to main AR page
        safeSetState(setSelectedLens, null);
        safeSetState(setShowLenses, true);
      }
      // If lens has no slug, keep it selected (user selected it manually and URL didn't change)
    }
  }, [urlSlug, lenses, selectedLens]);

  const cleanupLensSession = (isFullCleanup = false) => {
    try {
      const currentSession = sessionRef.current;
      const currentSelectedLens = selectedLensRef.current;
      const currentActiveStream = activeStreamRef.current;
      const currentCameraKitInstance = cameraKitInstanceRef.current;
      
      if (currentSession) {
        if (currentSelectedLens) {
          currentSession.removeLens().catch(err => console.warn('Error removing lens:', err));
        }
        currentSession.pause().catch(err => console.warn('Error pausing session:', err));
        
        if (isFullCleanup) {
          currentSession.destroy().catch(err => console.warn('Error destroying session:', err));
        }
      }
      
      if (currentActiveStream) {
        currentActiveStream.getTracks().forEach(track => {
          track.stop();
        });
        
        if (isFullCleanup) {
          activeStreamRef.current = null;
        }
      }
      
      if (isFullCleanup && currentCameraKitInstance) {
        cameraKitInstanceRef.current = null;
      }
      
      toggleHeaderVisibility(false);
      resetBodyStyles();
      
      document.querySelectorAll('audio, video').forEach(media => {
        media.pause();
        media.currentTime = 0;
      });
      
      const flash = document.getElementById('photo-flash');
      if (flash && document.body.contains(flash)) {
        document.body.removeChild(flash);
      }
      
      if (!isFullCleanup) {
        safeSetState(setSelectedLens, null);
        safeSetState(setShowLenses, true);
      }
    } catch (error) {
      console.warn('Error during lens session cleanup:', error);
    }
  };
  
  const setMediaStream = async (currentSession, facing = 'user') => {
    if (!currentSession) return;
    
    try {
      const streamConstraints = getCameraConstraints(isMobile, facing);
      const mediaStream = await navigator.mediaDevices.getUserMedia(streamConstraints);
      
      const transformOptions = {};
      if (facing === 'user') {
        transformOptions.transform = Transform2D.MirrorX;
      }
      
      const source = createMediaStreamSource(mediaStream, transformOptions);
      
      if (!currentSession) {
        if (mediaStream) {
          mediaStream.getTracks().forEach(track => track.stop());
        }
        return;
      }
      
      await currentSession.setSource(source);
      
      let camWidth = 0;
      let camHeight = 0;
      
      try {
        const tracks = mediaStream.getVideoTracks();
        if (tracks && tracks.length > 0) {
          const settings = tracks[0].getSettings();
          if (settings) {
            camWidth = settings.width || 0;
            camHeight = settings.height || 0;
          }
        }
      } catch (err) {
        console.warn("Error getting camera settings:", err);
      }
      
      const { w, h } = calculateRenderSize(isMobile, camWidth, camHeight);
      
      try {
        if (source && typeof source.setRenderSize === 'function') {
          await source.setRenderSize(w, h);
        }
      } catch (err) {
        console.warn("Error setting render size:", err);
      }
      
      activeStreamRef.current = mediaStream;
      
      return currentSession;
    } catch (err) {
      if (isMountedRef.current) {
        handleError(err, setError);
      } else {
        console.warn('Error setting media stream (component unmounted):', err);
      }
      return null;
    }
  };
  
  const loadLenses = async () => {
    try {
      const lensGroupIds = props.lenses && [...new Set(props.lenses.map(lens => lens.groupId).filter(Boolean))];
      const lensIds = props.lenses && [...new Set(props.lenses.map(lens => lens.lensId).filter(Boolean))];
      
      if (!lensGroupIds || lensGroupIds.length === 0) {
        throw new Error("No lens groups found in configuration");
      }

      const { lenses: allLoadedLenses } = await cameraKit.lensRepository.loadLensGroups(lensGroupIds);

      if (!allLoadedLenses || allLoadedLenses.length === 0) {
        throw new Error("No available lenses found");
      }
      
      const filteredLenses = allLoadedLenses.filter(lens => lensIds.includes(lens.id));
      
      if (filteredLenses.length === 0) {
        throw new Error("No configured lenses available");
      }
      
      const enrichedLenses = filteredLenses.map(snapLens => {
        const backendLens = props.lenses.find(bl => bl.lensId === snapLens.id);

        return {
          ...snapLens,
          defaultCamera: backendLens && backendLens.defaultCamera,
          backendConfig: backendLens
        };
      });
      
      safeSetState(setLenses, enrichedLenses);
      safeSetState(setShowLoading, false);
    } catch (err) {
      if (isMountedRef.current) {
        handleError(err, setError);
      } else {
        console.warn('Error loading lenses (component unmounted):', err);
      }
    }
  };

  const getHeaderHeight = useCallback(() => {
    const header = document.getElementById('header');
    const appNavigation = document.querySelector('.app-navigation');
    
    let totalHeight = 0;
    if (header) totalHeight += header.offsetHeight;
    if (appNavigation) totalHeight += appNavigation.offsetHeight;
    
    return totalHeight - 20;
  }, []);

  const scrollToCanvasTop = useCallback(() => {
    if (isMobile) {
      window.scrollTo(0, 0);
    } else {
      const headerHeight = getHeaderHeight();
      window.scrollTo({
        behavior: 'smooth', 
        top: headerHeight
      });
    }
  }, [isMobile, getHeaderHeight]);
  
  const handleLensSelect = async (lens) => {
    if (!session) return;
    
    try {
      safeSetState(setShowLoading, true);
      safeSetState(setShowLenses, false);
      
      scrollToCanvasTop();
      
      // Only update URL if we're not already on the correct URL
      const lensSlug = lens.backendConfig && lens.backendConfig.slug;
      const targetUrl = `${snapchatLensesRoute}/${lensSlug}`;
      
      if (lensSlug && props.history && props.location.pathname !== targetUrl) {
        props.history.push(targetUrl);
      }
      
      const useBackCamera = shouldUseBackCamera(lens);
      const cameraToUse = useBackCamera ? 'environment' : 'user';
      setCameraFacing(cameraToUse);
      
      const loadedLens = await cameraKit.lensRepository.loadLens(
        lens.id, 
        lens.groupId
      );

      if (!session.isRunning) {
        await session.play();
      }
      
      await session.removeLens();
      
      await setMediaStream(session, cameraToUse);
      
      await session.applyLens(loadedLens);

      safeSetState(setSelectedLens, lens);
      safeSetState(setShowLoading, false);
    } catch (err) {
      if (isMountedRef.current) {
        handleError(err, setError);
      } else {
        console.warn('Error selecting lens (component unmounted):', err);
      }
    }
  };
  
  const handleBackToLenses = async () => {
    if (!session) return;
    
    try {
      safeSetState(setShowLoading, true);

      await cleanupLensSession();
      
      props.history.push(snapchatLensesRoute);

      safeSetState(setShowLoading, false);
    } catch (err) {
      if (isMountedRef.current) {
        handleError(err, setError);
      } else {
        console.warn('Error going back to lenses (component unmounted):', err);
      }
    }
  };
  
  const handleCameraSwitch = async () => {
    if (!session) return;
    
    try {      
      safeSetState(setShowLoading, true);
      const newFacing = getOppositeCameraFacing(cameraFacing);
      setCameraFacing(newFacing);
      
      await setMediaStream(session, newFacing);
      
      if (selectedLens) {
        const loadedLens = await cameraKit.lensRepository.loadLens(
          selectedLens.id, 
          selectedLens.groupId
        );
        
        await session.removeLens();
        await session.applyLens(loadedLens);
      }

      safeSetState(setShowLoading, false);
    } catch (err) {
      if (isMountedRef.current) {
        handleError(err, setError);
      } else {
        console.warn('Error switching camera (component unmounted):', err);
      }
    }
  };
  
  useEffect(() => {
    return () => {
      isMountedRef.current = false; // Mark as unmounted
      cleanupLensSession(true);
    };
  }, []);
  
  useEffect(() => {
    const shouldBeFullscreen = isMobile && selectedLens;
    
    safeSetState(setIsFullscreenMode, shouldBeFullscreen);
    toggleHeaderVisibility(shouldBeFullscreen);
    
    let headerCheckInterval = null;
    
    if (shouldBeFullscreen) {
      setFullscreenBodyStyles();
      
      headerCheckInterval = setInterval(() => {
        toggleHeaderVisibility(true);
      }, TIMING.headerCheckInterval);
    } else {
      resetBodyStyles();
    }
    
    return () => {
      if (headerCheckInterval) {
        clearInterval(headerCheckInterval);
      }
      
      toggleHeaderVisibility(false);
      resetBodyStyles();
    };
  }, [isMobile, selectedLens]);
  
  const handleCapturePhoto = () => {
    try {
      const canvas = document.getElementById('snapchat-camera-canvas');
      if (!canvas) {
        console.error('Canvas no encontrado');
        return;
      }
      
      const flash = createPhotoFlash();
      document.body.appendChild(flash);
      
      setTimeout(() => {
        try {
          const photoData = canvas.toDataURL('image/jpeg', 0.95);
          console.log('Fotografía capturada');
          
          downloadPhoto(photoData)
            .catch(err => {
              console.error('Error al procesar la imagen:', err);
            });
            
          flash.style.opacity = '0';
          setTimeout(() => {
            if (document.body.contains(flash)) {
              document.body.removeChild(flash);
            }
          }, TIMING.flashDuration);
        } catch (captureErr) {
          console.error('Error en la captura:', captureErr);
          
          flash.style.opacity = '0';
          setTimeout(() => {
            if (document.body.contains(flash)) {
              document.body.removeChild(flash);
            }
          }, TIMING.flashDuration);
        }
      }, TIMING.captureDelay);
    } catch (err) {
      console.error('Error al iniciar la captura:', err);
    }
  };

  // Keep refs updated with current values for cleanup
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  
  useEffect(() => {
    selectedLensRef.current = selectedLens;
  }, [selectedLens]);

  return (
    <ContentContainer fullWidth={!showLenses}>
      <Content fullWidth={!showLenses}>
        {showLenses && (
          <>
            <TitleSection inLensView={false} inLeftColumn={false} />
            
            <Subtitle>
              Explora aquí los lentes que tenemos para ti y diviértete con ellos.
            </Subtitle>
        
        <LensesContainer>
              {error ? (
                <ErrorContainer>
                  <ErrorMessage>{error.message}</ErrorMessage>
                  {!isProduction && error.details && (
                    <TechnicalErrorDetails>{error.details}</TechnicalErrorDetails>
                  )}
                </ErrorContainer>
              ) : (
                <>
                  <LensesGrid ref={lensesGridRef}>
                    {lenses.map(lens => (
                      <LensItem key={lens.id} onClick={() => handleLensSelect(lens)}>
                        <img src={lens.iconUrl} alt={lens.name} />
                        <LensName>{lens.name}</LensName>
                      </LensItem>
                    ))}
                  </LensesGrid>
                  
                  {showLoading && <LogoLoading/>}
                </>
              )}
        </LensesContainer>
          </>
        )}
        
        <TwoColumnsContainer visible={!showLenses} className={isFullscreenMode ? 'fullscreen-mode' : ''}>
          <LeftColumn>
            <BackLink onClick={handleBackToLenses}>
              <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2L2 10L10 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Lentes
            </BackLink>
            
            <TitleSection inLensView={true} inLeftColumn={true} />
          </LeftColumn>
          
          <RightColumn>
            <PreviewContainer visible={true}>
              <CanvasContainer className={isFullscreenMode ? 'fullscreen-canvas' : ''}>
                <div className="canvas-wrapper" style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                  <Canvas 
                    ref={canvasRef} 
                    id="snapchat-camera-canvas"
                    width={CANVAS_DIMENSIONS.width}
                    height={CANVAS_DIMENSIONS.height}
                  />
                </div>
                
                {selectedLens && (
                  <CanvasControls 
                    onBack={handleBackToLenses}
                    onCameraSwitch={handleCameraSwitch}
                    onCapture={handleCapturePhoto}
                    selectedLens={selectedLens}
                  />
                )}
              </CanvasContainer>
              
              {!isMobile && (
                <ControlsContainer>
                  <SelectedLensName>
                    {selectedLens && selectedLens.name}
                  </SelectedLensName>
                </ControlsContainer>
              )}
            </PreviewContainer>
          </RightColumn>
        </TwoColumnsContainer>
        
        {showLoading && <LogoLoading/>}
      </Content>
    </ContentContainer>
  );
};

const SnapchatLensesPage = (props) => {
  return (
    <FullColumnLayout 
      Content={() => <SnapchatLensesContent {...props} />} 
      bgWhite={true}
      includeSearch={false}
      headerTransparent={false}
    />
  );
};

const mapStateToProps = (state) => ({
  apiToken: getSnapchatApiToken(state),
  isActive: isSnapchatEnabled(state),
  lenses: getSnapchatLenses(state),
});

export default withRouter(connect(mapStateToProps)(SnapchatLensesPage))